import { useEffect, useRef, useState } from 'react';

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

interface CountUpValueProps {
  value: string;
  unit?: string;
}

export function CountUpValue({ value, unit }: CountUpValueProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [displayNum, setDisplayNum] = useState(0);
  const hasStarted = useRef(false);

  // Extracts optional prefix (e.g. "+"), the integer, and any trailing non-digit chars
  // "2 À 5" won't match (non-digit between digits) → static display
  const match = value.match(/^([^0-9]*)(\d+)([^0-9]*)$/);
  const isAnimatable = !!match;
  const prefix = match ? match[1] : '';
  const targetNum = match ? parseInt(match[2], 10) : 0;
  const inlineSuffix = match ? match[3] : '';

  useEffect(() => {
    if (!isAnimatable) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasStarted.current) return;
        hasStarted.current = true;
        observer.disconnect();

        const duration = 1400;
        const startTime = performance.now();

        const tick = (now: number) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          setDisplayNum(Math.round(easeOutCubic(progress) * targetNum));
          if (progress < 1) requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
      },
      { threshold: 0.35 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [isAnimatable, targetNum]);

  if (!isAnimatable) {
    return (
      <div className="font-black uppercase leading-none">
        <span className="text-5xl lg:text-6xl">{value}</span>
        {unit && <span className="text-2xl lg:text-3xl ml-2">{unit}</span>}
      </div>
    );
  }

  return (
    <div ref={ref} className="font-black uppercase leading-none">
      <span className="text-5xl lg:text-6xl">
        {prefix}{displayNum}{inlineSuffix}
      </span>
      {unit && <span className="text-2xl lg:text-3xl ml-2">{unit}</span>}
    </div>
  );
}
